import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Relance, Relances as RelancesContrat } from '../lib/contrat';
import { useRequete } from '../lib/requete';
import { dateHeure, delaiRestant, etatRelance } from '../lib/format';
import { urlConversationFront, type ContexteFront } from '../lib/front';
import { Badge, Bandeau, ErreurAffichee, Section, Squelette, Vide } from './ui';
import { BoiteActions } from './Actions';

/** Ordre d'affichage : ce qui est encore rattrapable d'abord. */
const ORDRE: Record<string, number> = {
  brouillon: 0,
  programme: 1,
  echec: 2,
  envoye: 3,
  repondu: 4,
  annule: 5,
};

const TON: Record<string, 'neutre' | 'info' | 'succes' | 'attention' | 'critique'> = {
  brouillon: 'attention',
  programme: 'attention',
  envoye: 'info',
  repondu: 'succes',
  annule: 'neutre',
  echec: 'critique',
};

/**
 * Onglet « Relances » : ce que le pipeline a rédigé ou envoyé au nom du
 * collaborateur. Sans cette vue, personne n'acceptera durablement l'envoi
 * automatique — c'est la contrepartie de la délégation.
 */
export function Relances({
  contexteFront,
  teammateId,
  auteur,
}: {
  contexteFront: ContexteFront | undefined;
  teammateId: string | undefined;
  auteur?: string;
}) {
  const { donnees, erreur, chargement, rafraichir } = useRequete<RelancesContrat>(
    (signal, forcer) => api.relances(teammateId as string, signal, forcer),
    [teammateId],
    Boolean(teammateId),
  );

  if (!teammateId) {
    return <Vide>Front n’a pas communiqué votre identifiant de collaborateur.</Vide>;
  }
  if (chargement) return <Squelette lignes={5} />;
  if (erreur) return <ErreurAffichee erreur={erreur} onReessayer={rafraichir} />;
  if (!donnees) return <Vide>Rien à afficher.</Vide>;

  const elements = [...donnees.elements].sort((a, b) => {
    const rang = (ORDRE[a.etat] ?? 9) - (ORDRE[b.etat] ?? 9);
    return rang !== 0 ? rang : b.cree_le.localeCompare(a.cree_le);
  });

  const compteurs = Object.entries(donnees.compteurs).filter(([, valeur]) => (valeur ?? 0) > 0);

  return (
    <div className="pile">
      <header className="entete">
        <div className="entete-tete">
          <h1>Relances en mon nom</h1>
          <button type="button" className="bouton bouton--discret" onClick={rafraichir}>
            Recharger
          </button>
        </div>
        {compteurs.length > 0 && (
          <div className="compteurs">
            {compteurs.map(([etat, valeur]) => (
              <span key={etat} className="compteur">
                <Badge ton={TON[etat] ?? 'neutre'}>{valeur}</Badge>
                {etatRelance(etat)}
              </span>
            ))}
          </div>
        )}
      </header>

      {elements.length === 0 ? (
        <Vide>Aucune relance rédigée en votre nom.</Vide>
      ) : (
        <Section>
          <ul className="relances">
            {elements.map((relance) => (
              <LigneRelance
                key={relance.id}
                relance={relance}
                contexteFront={contexteFront}
                auteur={auteur}
                onFait={rafraichir}
              />
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function LigneRelance({
  relance,
  contexteFront,
  auteur,
  onFait,
}: {
  relance: Relance;
  contexteFront: ContexteFront | undefined;
  auteur?: string;
  onFait?: () => void;
}) {
  const delai = useDelai(relance.annulable_jusqua);
  const encoreAnnulable = delai !== null && delai !== 'échéance passée';
  // Capture locale : la narration de type ne survit pas à la fermeture du
  // gestionnaire de clic sur une propriété d'objet.
  const cnvId = relance.cnv_id;

  return (
    <li className={`relance relance--${relance.etat} avec-actions`}>
      <div>
      <div className="relance-tete">
        <Badge ton={TON[relance.etat] ?? 'neutre'}>{etatRelance(relance.etat)}</Badge>
        {relance.client && <span className="relance-client">{relance.client}</span>}
      </div>

      <p className="relance-objet">{relance.objet}</p>
      <p className="relance-meta">
        <span>À {relance.destinataire}</span>
        {relance.envoi_prevu_le && (
          <span>
            {relance.etat === 'envoye' ? 'Envoyée le ' : 'Envoi prévu le '}
            {dateHeure(relance.envoi_prevu_le)}
          </span>
        )}
      </p>

      {relance.extrait && <blockquote className="relance-extrait">{relance.extrait}</blockquote>}
      {relance.motif && <p className="relance-motif">Motif : {relance.motif}</p>}

      {encoreAnnulable && (
        <Bandeau ton="attention">
          Annulable jusqu’au {dateHeure(relance.annulable_jusqua)} — {delai}.
        </Bandeau>
      )}

      {/*
        L'annulation reste ici, et pas dans la boîte : elle ne vaut que tant
        que le délai court, et elle est propre à cet écran. La boîte porte les
        six gestes qu'on retrouve partout, celui-ci n'en est pas un.
      */}
      {encoreAnnulable && (
        <p className="relance-actions">
          <button
            type="button"
            className="bouton"
            disabled
            title="L’annulation depuis le plugin arrivera avec l’API d’écriture."
          >
            Annuler l’envoi
          </button>
        </p>
      )}
      </div>

      <BoiteActions
        cible={{
          conv_id: cnvId,
          client: relance.client,
          lien_front: cnvId ? urlConversationFront(cnvId) : null,
        }}
        contexteFront={contexteFront}
        auteur={auteur}
        onFait={onFait}
        compact
      />
    </li>
  );
}

/**
 * Délai restant, réévalué chaque minute : un compte à rebours figé sur une
 * échéance d'annulation serait pire que pas de compte à rebours du tout.
 */
function useDelai(echeance: string | null): string | null {
  const [delai, setDelai] = useState(() => delaiRestant(echeance));

  useEffect(() => {
    setDelai(delaiRestant(echeance));
    if (!echeance) return;
    const minuterie = window.setInterval(() => setDelai(delaiRestant(echeance)), 60_000);
    return () => window.clearInterval(minuterie);
  }, [echeance]);

  return delai;
}
